import { useCallback, useRef, useState } from 'react';
import { Checkout } from '../types/checkout';
import { Api } from './useApi';
import { MetodoPagamentoVendaEnum } from '../types/enums/metodo-pagamento.enum';
import { MoedaVendaDescricao, MoedaVendaEnum } from '../types/enums/moeda-venda.enum';
import { getAllUrlParams, getUrlParams } from '../utils/get-url-params';
import { emitUtmifyEvent } from '../utils/emitUtmifyEvent';
import { getConfig } from '../utils/get-config';

type Params = {
  checkout: Checkout;
  api: Api;
  gerarTokenRecaptcha: (path: string) => Promise<string>;
  onSuccess: (vendaId: string, vendaToken: string) => void;
  onError: (msg: string) => void;
};

type Result = {
  paypalAvailable: boolean;
  paypalLoading: boolean;
  paypalCurrency: string;
  createOrder: () => Promise<string>;
  onApprove: (data: { orderID: string }) => Promise<void>;
  onCancel: () => void;
};

export const usePayPalExpress = ({
  checkout,
  api,
  gerarTokenRecaptcha,
  onSuccess,
  onError,
}: Params): Result => {
  const [paypalLoading, setPaypalLoading] = useState(false);
  const vendaIdRef = useRef<string | null>(null);
  const vendaTokenRef = useRef<string | null>(null);

  const sdkConfigured = !!getConfig('REACT_APP_PAYPAL_CLIENT_ID');

  const paypalAvailable =
    sdkConfigured &&
    !!checkout?.id &&
    !!checkout?.metodosPagamento?.includes('paypal') &&
    !checkout?.recorrencia;

  const paypalCurrency =
    MoedaVendaDescricao[checkout?.moeda ?? MoedaVendaEnum.BRL] || 'BRL';

  const createOrder = useCallback(async (): Promise<string> => {
    setPaypalLoading(true);
    try {
      const recaptchaToken = await gerarTokenRecaptcha('/venda/paypal/create_order');
      const afiliadoId = getUrlParams('affiliate') ?? undefined;

      const res = await api.Venda.createPaypalOrder({
        metodoPagamento: MetodoPagamentoVendaEnum.PAY_PAL,
        produtoId: checkout.produtoId ?? '',
        emailComprador: '',
        nomeComprador: '',
        sobrenomeComprador: '',
        cpfComprador: '',
        telefoneComprador: '',
        checkoutId: checkout.id,
        recaptchaToken,
        afiliadoId,
        utmSource: getUrlParams('utm_source'),
        utmCampaign: getUrlParams('utm_campaign'),
        utmMedium: getUrlParams('utm_medium'),
        utmContent: getUrlParams('utm_content'),
        utmTerm: getUrlParams('utm_term'),
        urlParams: getAllUrlParams(),
      });

      if (res.error || !res.data?.orderId) {
        const msg = (res as any).message?.[0] ?? 'Erro ao criar pedido PayPal.';
        onError(msg);
        throw new Error(msg);
      }

      vendaIdRef.current = res.data.vendaId;
      vendaTokenRef.current = res.data.vendaToken;
      emitUtmifyEvent({
        event: 'waiting_payment',
        vendaId: res.data.vendaId,
      });
      return res.data.orderId;
    } catch (err: any) {
      setPaypalLoading(false);
      throw err;
    }
  }, [checkout?.id, checkout?.produtoId, api, gerarTokenRecaptcha, onError]);

  const onApprove = useCallback(
    async (data: { orderID: string }) => {
      try {
        const vendaId = vendaIdRef.current;
        if (!vendaId) {
          onError('Venda não encontrada.');
          return;
        }

        await api.Venda.capturePaypal({
          vendaId,
          paypalOrderId: data.orderID,
        });

        onSuccess(vendaId, vendaTokenRef.current ?? '');
      } catch (err: any) {
        console.error('[PayPalExpress] Erro ao capturar:', err);
        onError(
          err?.message || 'Erro ao processar pagamento PayPal. Tente novamente.',
        );
      } finally {
        setPaypalLoading(false);
      }
    },
    [api, onSuccess, onError],
  );

  const onCancel = useCallback(() => {
    setPaypalLoading(false);
  }, []);

  return { paypalAvailable, paypalLoading, paypalCurrency, createOrder, onApprove, onCancel };
};
